'use client'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ShopifyTopupDialog } from './shopify-topup-dialog'
import { TopupLifetimeNote } from './topup-lifetime-note'
import { usePurchaseShopifyTopupMutation } from '@/hooks/mutations/shopify-topups'
import { useShopifyTopupOptionsQuery } from '@/hooks/queries/shopify-topups'
import { formatCredits } from '@/lib/billing/utils/credits'
import type { ShopifyTopupOption, ShopifyTopupOptions } from '@/lib/billing/topup/shopify-topup-service'

const UNAVAILABLE_MESSAGES: Record<NonNullable<ShopifyTopupOptions['unavailableReason']>, string> = {
  no_open_cycle: 'Top-ups become available once your Shopify trial ends and billing starts.',
  subscription_inactive: 'An active Shopify subscription is required before topping up.',
  billing_store_unavailable:
    'Connect and designate your Shopify billing store before topping up.',
  contract_unavailable:
    'Your Shopify subscription could not be read just now. Refresh the page to try again.',
  not_shopify_channel: 'This account is not billed through Shopify.',
}

/**
 * Shopify top-ups. The amounts come from the `topup-*` usage meters on the merchant's
 * own plan, so there is no custom-amount input — Shopify caps a plan at five meters.
 */
export function ShopifyTopupCard({
  onBalanceChanged,
  lifetimeMonths,
}: {
  onBalanceChanged: () => void
  /** Months top-up credits last; null = never. */
  lifetimeMonths: number | null
}) {
  const { data, isLoading, isError } = useShopifyTopupOptionsQuery(true)
  const purchase = usePurchaseShopifyTopupMutation(onBalanceChanged)
  const [selected, setSelected] = useState<ShopifyTopupOption | null>(null)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Top Up Balance</CardTitle>
      </CardHeader>
      <CardContent>
        <Body
          data={data}
          isLoading={isLoading}
          isError={isError}
          pendingHandle={purchase.isPending ? purchase.variables?.meterHandle ?? null : null}
          lifetimeMonths={lifetimeMonths}
          onSelect={setSelected}
        />
      </CardContent>

      <ShopifyTopupDialog
        option={selected}
        open={selected !== null}
        onOpenChange={(open) => { if (!open && !purchase.isPending) setSelected(null) }}
        pending={purchase.isPending}
        lifetimeMonths={lifetimeMonths}
        onConfirm={() => {
          if (!selected) return
          purchase.mutate(
            { meterHandle: selected.meterHandle },
            { onSettled: () => setSelected(null) },
          )
        }}
      />
    </Card>
  )
}

function Body({
  data,
  isLoading,
  isError,
  pendingHandle,
  lifetimeMonths,
  onSelect,
}: {
  data: ReturnType<typeof useShopifyTopupOptionsQuery>['data']
  isLoading: boolean
  isError: boolean
  pendingHandle: string | null
  lifetimeMonths: number | null
  onSelect: (option: ShopifyTopupOption) => void
}) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground">
        Your Shopify subscription could not be read just now. Refresh the page to try again.
      </p>
    )
  }

  // Paused, not gone: the amounts stay on screen so the merchant can see what will be
  // available again, and the reason stays deliberately generic — the operator-facing one
  // names internal attempt state.
  if (data.blocked) {
    return (
      <>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Top-ups are paused while we review a billing discrepancy on your account. Contact
            support to resume them.
          </AlertDescription>
        </Alert>
        {data.options.length > 0 && (
          <TopupGrid options={data.options} pendingHandle={null} disabled onSelect={onSelect} />
        )}
      </>
    )
  }

  if (data.unavailableReason) {
    return <p className="text-sm text-muted-foreground">{UNAVAILABLE_MESSAGES[data.unavailableReason]}</p>
  }

  if (data.options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Your Shopify plan does not offer balance top-ups. Contact support if you need to add balance.
      </p>
    )
  }

  const affordable = data.options.filter((option) => option.priceCents <= data.capRemainingCents)
  if (affordable.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        You have reached the top-up limit for this billing cycle. It resets once Shopify bills the
        cycle; contact support if you need more before then.
      </p>
    )
  }

  return (
    <>
      <TopupGrid options={affordable} pendingHandle={pendingHandle} onSelect={onSelect} />
      <p className="mt-4 text-xs text-muted-foreground">
        Charges are added to your store&apos;s next Shopify invoice.
      </p>
      <TopupLifetimeNote months={lifetimeMonths} className="mt-1 text-xs text-muted-foreground" />
    </>
  )
}

function TopupGrid({
  options,
  pendingHandle,
  disabled = false,
  onSelect,
}: {
  options: ShopifyTopupOption[]
  pendingHandle: string | null
  disabled?: boolean
  onSelect: (option: ShopifyTopupOption) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      {options.map((option) => (
        <div key={option.meterHandle} className="border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold break-words">{formatCredits(option.creditMicrousd)}</div>
          <div className="text-xs text-muted-foreground"><span>for</span> {option.label}</div>
          {option.bonusPercent > 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              <span>includes</span> {option.bonusPercent}<span>% bonus</span>
            </div>
          )}
          <Button
            className="mt-3 w-full"
            size="sm"
            onClick={() => onSelect(option)}
            disabled={disabled || pendingHandle !== null}
          >
            {pendingHandle === option.meterHandle && (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            <span>Buy</span>
          </Button>
        </div>
      ))}
    </div>
  )
}
